class DogAvenueReviewsExpand extends HTMLElement {
	connectedCallback() {
		this.toggleBtn = this.querySelector('[data-da-reviews-toggle]');
		this.panel = this.querySelector('[data-da-reviews-panel]');
		this.emptyState = this.querySelector('[data-da-reviews-empty]');
		this.debugEl = this.querySelector('[data-da-reviews-debug]');
		this.source = this.querySelector('[data-da-judge-source]');
		this.row1Track = this.querySelector('[data-da-row-track="1"]');
		this.row2Track = this.querySelector('[data-da-row-track="2"]');
		this.rowViewports = Array.from(this.querySelectorAll('[data-da-row-viewport]'));

		this.row1Speed = this._clampNumber(this.dataset.speedRow1, 12, 80, 36);
		this.row2Speed = this._clampNumber(this.dataset.speedRow2, 12, 80, 28);
		this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
		this.verifiedLabel = this.dataset.verifiedLabel || 'Verified customer';
		this.judgeHeaderPresent = this.dataset.judgeHeader === 'true';
		this.judgeChunkCount = Number(this.dataset.judgeChunks || 0);
		this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this._isOpen = false;
		this._hydrated = false;
		this._rafId = 0;
		this._lastTs = 0;
		this._running = false;
		this._rowState = [];

		this._onToggle = this._onToggle.bind(this);
		this._tick = this._tick.bind(this);
		this._onVisibility = this._onVisibility.bind(this);
		this._onResize = this._debounce(this._onResize.bind(this), 120);

		if (this.toggleBtn) {
			this.toggleBtn.addEventListener('click', this._onToggle);
		}

		document.addEventListener('visibilitychange', this._onVisibility);
		window.addEventListener('resize', this._onResize);
		if (this.debugEl) this.debugEl.hidden = true;
	}

	disconnectedCallback() {
		if (this.toggleBtn) {
			this.toggleBtn.removeEventListener('click', this._onToggle);
		}
		document.removeEventListener('visibilitychange', this._onVisibility);
		window.removeEventListener('resize', this._onResize);
		this._stop();
		if (this._observer) {
			this._observer.disconnect();
		}
		if (this._resizeObserver) {
			this._resizeObserver.disconnect();
		}
	}

	_onToggle() {
		if (!this.panel || !this.toggleBtn) return;

		this._isOpen = !this._isOpen;
		this.toggleBtn.setAttribute('aria-expanded', this._isOpen ? 'true' : 'false');
		this.toggleBtn.textContent = this._isOpen
			? this.toggleBtn.dataset.labelClose || 'Show less'
			: this.toggleBtn.dataset.labelOpen || 'Read more reviews';

		if (this._isOpen) {
			this.panel.hidden = false;
			this.panel.classList.add('is-open');
			this._hydrateWhenReady();
		} else {
			this.panel.classList.remove('is-open');
			this._stop();
			window.setTimeout(() => {
				if (!this._isOpen) this.panel.hidden = true;
			}, 360);
		}
	}

	_hydrateWhenReady() {
		if (this._hydrated) {
			window.requestAnimationFrame(() => {
				if (!this._isOpen) return;
				this._remeasureRows();
				this._startIfPossible();
			});
			return;
		}

		const existing = this._extractReviews();
		if (existing.length > 0) {
			this._setDebug(`Judge.me loaded ${existing.length} review(s).`);
			this._renderRows(existing);
			return;
		}

		if (!this.source) {
			this._showEmpty('No Judge.me source container found.');
			return;
		}

		const existingNodes = this.source.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__review, .jdgm-carousel-item').length;
		this._setDebug(
			`Waiting for Judge.me reviews (header: ${this.judgeHeaderPresent ? 'yes' : 'no'}, chunks: ${this.judgeChunkCount}, nodes: ${existingNodes}).`,
		);

		this._observer = new MutationObserver(() => {
			const reviews = this._extractReviews();
			if (reviews.length > 0) {
				this._observer.disconnect();
				this._setDebug(`Judge.me hydrated ${reviews.length} review(s).`);
				this._renderRows(reviews);
			}
		});
		this._observer.observe(this.source, { childList: true, subtree: true });

		window.setTimeout(() => {
			if (this._hydrated) return;
			const delayed = this._extractReviews();
			if (delayed.length > 0) {
				if (this._observer) this._observer.disconnect();
				this._setDebug(`Judge.me hydrated ${delayed.length} review(s) after delay.`);
				this._renderRows(delayed);
				return;
			}
			if (this._observer) this._observer.disconnect();
			this._showEmpty(
				`No reviews found after wait (header: ${this.judgeHeaderPresent ? 'yes' : 'no'}, chunks: ${this.judgeChunkCount}).`,
			);
		}, 4500);
	}

	_extractReviews() {
		if (!this.source) return [];
		const items = Array.from(
			this.source.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__review, .jdgm-carousel-item'),
		);
		if (!items.length) return [];

		return items
			.map((item) => {
				const title = this._safeText(
					item.querySelector('.jdgm-rev__title, .jdgm-carousel-item__review-title'),
				);
				const quote = this._safeText(
					item.querySelector('.jdgm-rev__body, .jdgm-carousel-item__review-body, .jdgm-rev__body p'),
				);
				const author = this._safeText(
					item.querySelector(
						'.jdgm-rev__author, .jdgm-rev__author-wrapper, .jdgm-carousel-item__reviewer-name',
					),
				);
				const img = item.querySelector('.jdgm-rev__pic-img, .jdgm-carousel-item__reviewer-avatar img');
				const imageSrc = img && img.getAttribute('src') ? img.getAttribute('src') : '';
				const imageAlt = img && img.getAttribute('alt') ? img.getAttribute('alt') : author;
				const stars = this._extractStars(item);

				return { title, quote, author, imageSrc, imageAlt, stars };
			})
			.filter((review) => review.quote || review.title || review.author);
	}

	_extractStars(item) {
		const onStars = item.querySelectorAll(
			'.jdgm-star.jdgm--on, .jdgm-star.jdgm--full, .jdgm-carousel-item__rating .jdgm-star',
		).length;
		if (onStars > 0) return Math.min(5, onStars);

		const ratingAttr = item.querySelector('[data-score]')?.getAttribute('data-score');
		const score = Number(ratingAttr);
		if (Number.isFinite(score)) return Math.max(0, Math.min(5, Math.round(score)));
		return 5;
	}

	_renderRows(reviews) {
		if (!this.row1Track || !this.row2Track) return;
		const padded = this._repeatToMinimum(reviews, 8);
		const midpoint = Math.ceil(padded.length / 2);
		const row1 = padded.slice(0, midpoint);
		const row2 = padded.slice(midpoint);
		const row2Safe = row2.length ? row2 : row1;
		const row1Dense = this._densifyRowSource(row1, 8);
		const row2Dense = this._densifyRowSource(row2Safe, 12);

		this._buildTrack(this.row1Track, row1Dense);
		this._buildTrack(this.row2Track, row2Dense);

		this._rowState = [
			this._createRowState(this.row1Track, -1, this.row1Speed),
			this._createRowState(this.row2Track, 1, this.row2Speed),
		].filter(Boolean);

		this._hydrated = this._rowState.length > 0;
		if (!this._hydrated) {
			this._showEmpty('Review rows could not be measured for marquee.');
			return;
		}

		this._bindDynamicRemeasure();

		this._startIfPossible();
	}

	_bindDynamicRemeasure() {
		if (this._resizeObserver) this._resizeObserver.disconnect();

		if (typeof ResizeObserver !== 'undefined') {
			const debouncedRemeasure = this._debounce(() => this._remeasureRows(), 100);
			this._resizeObserver = new ResizeObserver(() => debouncedRemeasure());

			[this.row1Track, this.row2Track].forEach((track) => {
				if (!track) return;
				if (track.firstElementChild) this._resizeObserver.observe(track.firstElementChild);
				this._resizeObserver.observe(track);
			});

			this.rowViewports.forEach((viewport) => this._resizeObserver.observe(viewport));
		}

		const onImageReady = this._debounce(() => this._remeasureRows(), 40);
		const images = this.querySelectorAll('[data-da-row-track] img');
		images.forEach((img) => {
			if (img.complete) return;
			img.addEventListener('load', onImageReady, { once: true });
			img.addEventListener('error', onImageReady, { once: true });
		});
	}

	_buildSet(reviews) {
		const set = document.createElement('div');
		set.className = 'dog-avenue-t__marquee-set';
		reviews.forEach((review) => set.appendChild(this._createCard(review)));
		return set;
	}

	_buildTrack(track, reviews) {
		track.innerHTML = '';
		track.appendChild(this._buildSet(reviews));
		this._ensureTrackCoverage(track);
	}

	_getRequiredCopies(track, setWidth) {
		const viewportWidth = track.parentElement?.getBoundingClientRect().width || 0;
		if (!Number.isFinite(setWidth) || setWidth <= 0) return 2;

		// Ensure enough repeated sets are always available beyond the viewport
		// so the wrap seam never exposes blank space.
		return Math.max(5, Math.ceil((viewportWidth * 3) / setWidth) + 1);
	}

	_ensureTrackCoverage(track) {
		const firstSet = track.firstElementChild;
		if (!firstSet) return 0;
		const setWidth = firstSet.getBoundingClientRect().width;
		if (!Number.isFinite(setWidth) || setWidth <= 0) return 0;

		const requiredCopies = this._getRequiredCopies(track, setWidth);
		while (track.children.length < requiredCopies) {
			track.appendChild(firstSet.cloneNode(true));
		}
		return setWidth;
	}

	_createCard(review) {
		const article = document.createElement('article');
		article.className = 'dog-avenue-t__card dog-avenue-t__card--marquee';

		if (review.imageSrc) {
			const img = document.createElement('img');
			img.className = 'dog-avenue-t__avatar';
			img.src = review.imageSrc;
			img.alt = review.imageAlt || review.author || 'Review photo';
			img.loading = 'lazy';
			article.appendChild(img);
		} else {
			article.classList.add('dog-avenue-t__card--no-avatar');
		}

		const starsWrap = document.createElement('div');
		starsWrap.className = 'dog-avenue-t__stars';
		starsWrap.setAttribute('role', 'img');
		starsWrap.setAttribute('aria-label', `${review.stars} out of 5 stars`);
		for (let i = 1; i <= 5; i += 1) {
			const star = document.createElement('span');
			star.className = i <= review.stars ? 'dog-avenue-t__star' : 'dog-avenue-t__star dog-avenue-t__star--empty';
			star.textContent = '★';
			starsWrap.appendChild(star);
		}
		article.appendChild(starsWrap);

		if (review.title) {
			const title = document.createElement('h3');
			title.className = 'dog-avenue-t__title';
			title.textContent = review.title;
			article.appendChild(title);
		}

		if (review.quote) {
			const quote = document.createElement('p');
			quote.className = 'dog-avenue-t__quote';
			quote.textContent = review.quote;
			article.appendChild(quote);
		}

		const footer = document.createElement('div');
		footer.className = 'dog-avenue-t__footer';
		if (review.author) {
			const author = document.createElement('p');
			author.className = 'dog-avenue-t__author';
			author.textContent = review.author;
			footer.appendChild(author);
		}
		const verified = document.createElement('p');
		verified.className = 'dog-avenue-t__verified';
		verified.textContent = this.verifiedLabel;
		footer.appendChild(verified);
		article.appendChild(footer);

		return article;
	}

	_createRowState(track, direction, speed) {
		const firstSet = track.firstElementChild;
		if (!firstSet) return null;
		const setWidth = firstSet.getBoundingClientRect().width;
		if (!Number.isFinite(setWidth) || setWidth <= 0) return null;
		const tx = direction > 0 ? -setWidth : 0;
		return { track, setWidth, direction, speed, tx };
	}

	_startIfPossible() {
		if (!this._isOpen || this.reducedMotion || this._running || !this._rowState.length) return;
		this._running = true;
		this._lastTs = 0;
		this._rafId = requestAnimationFrame(this._tick);
	}

	_stop() {
		this._running = false;
		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
			this._rafId = 0;
		}
	}

	_tick(ts) {
		this._rafId = 0;
		if (!this._running || !this._rowState.length) return;
		if (!this._lastTs) this._lastTs = ts;
		const dt = Math.min(0.064, (ts - this._lastTs) / 1000);
		this._lastTs = ts;

		this._rowState.forEach((row) => {
			row.tx += row.direction * row.speed * dt;
			if (row.direction < 0) {
				while (row.tx <= -row.setWidth) row.tx += row.setWidth;
				while (row.tx > 0) row.tx -= row.setWidth;
			} else {
				while (row.tx >= 0) row.tx -= row.setWidth;
				while (row.tx < -row.setWidth) row.tx += row.setWidth;
			}
			row.track.style.transform = `translate3d(${row.tx}px, 0, 0)`;
		});

		// Runtime guardrail: if layout shifts reduce effective coverage,
		// add copies and remeasure without waiting for a visible seam.
		if (!this._lastCoverageCheckTs || ts - this._lastCoverageCheckTs > 900) {
			this._lastCoverageCheckTs = ts;
			this._ensureTrackCoverage(this.row1Track);
			this._ensureTrackCoverage(this.row2Track);
			this._remeasureRows();
		}

		this._rafId = requestAnimationFrame(this._tick);
	}

	_onVisibility() {
		if (document.hidden) {
			this._stop();
		} else {
			this._startIfPossible();
		}
	}

	_onResize() {
		if (!this._hydrated || !this._isOpen) return;
		this._remeasureRows();
		if (this._isOpen && !this.reducedMotion) {
			this._stop();
			this._startIfPossible();
		}
	}

	_remeasureRows() {
		if (!this._isOpen) return;
		if (this.row1Track) this._ensureTrackCoverage(this.row1Track);
		if (this.row2Track) this._ensureTrackCoverage(this.row2Track);

		const previousStateByTrack = new Map(this._rowState.map((row) => [row.track, row]));
		this._rowState = [
			this._createRowState(this.row1Track, -1, this.row1Speed),
			this._createRowState(this.row2Track, 1, this.row2Speed),
		].filter(Boolean);
		if (!this._rowState.length) {
			return;
		}

		this._rowState.forEach((row) => {
			const prev = previousStateByTrack.get(row.track);
			if (prev && prev.setWidth > 0 && row.setWidth > 0) {
				const ratio = prev.tx / prev.setWidth;
				row.tx = ratio * row.setWidth;
				if (row.direction < 0) {
					while (row.tx <= -row.setWidth) row.tx += row.setWidth;
					while (row.tx > 0) row.tx -= row.setWidth;
				} else {
					while (row.tx >= 0) row.tx -= row.setWidth;
					while (row.tx < -row.setWidth) row.tx += row.setWidth;
				}
			}
			row.track.style.transform = `translate3d(${row.tx || 0}px, 0, 0)`;
		});
	}

	_showEmpty(_reason) {
		this._stop();
	}

	_setDebug(_message) {
		// Intentionally disabled in storefront.
		// Keep hook for future editor-only troubleshooting.
		return;
	}

	_repeatToMinimum(items, min) {
		if (!items.length) return [];
		if (items.length >= min) return items;
		const out = [];
		while (out.length < min) {
			out.push(...items);
		}
		return out.slice(0, min);
	}

	_densifyRowSource(items, minCards) {
		return this._repeatToMinimum(items, minCards);
	}

	_safeText(el) {
		return el && el.textContent ? el.textContent.trim() : '';
	}

	_clampNumber(value, min, max, fallback) {
		const n = Number(value);
		if (!Number.isFinite(n)) return fallback;
		return Math.max(min, Math.min(max, n));
	}

	_debounce(fn, wait) {
		let timeout;
		return (...args) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => fn.apply(this, args), wait);
		};
	}
}

if (!window.customElements.get('dog-avenue-reviews-expand')) {
	window.customElements.define('dog-avenue-reviews-expand', DogAvenueReviewsExpand);
}
